import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Flame, Trophy, Sparkles, Lock, Share2, Check, X, Loader2,
  MessageCircle, ArrowRight, Crown, Compass, Map as MapIcon, Users,
  ChevronRight, Copy
} from "lucide-react";
import { BQ, getStoredUsername, storeUsername, clearUsername } from "../lib/bharatQuest";
import RegionMap from "../components/quest/RegionMap";
import PersonalityChat from "../components/quest/PersonalityChat";
import Leaderboard from "../components/quest/Leaderboard";

const TABS = [
  { id: "quest", label: "Daily Quest", icon: Sparkles },
  { id: "map", label: "Civilization Map", icon: MapIcon },
  { id: "personalities", label: "Talk to Legends", icon: Users },
  { id: "leaderboard", label: "Leaderboard", icon: Trophy },
];

export default function BharatQuest() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("quest");
  const [showRegister, setShowRegister] = useState(false);

  const reload = useCallback(async () => {
    const u = getStoredUsername();
    if (!u) { setUser(null); setLoading(false); return; }
    try {
      const me = await BQ.me(u);
      setUser(me);
    } catch (e) {
      clearUsername();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  if (loading) {
    return (
      <div className="mythos-ink-bg min-h-[80vh] flex items-center justify-center" data-testid="bq-loading">
        <Loader2 className="animate-spin text-[#C9A66B]" size={32} />
      </div>
    );
  }

  if (!user || showRegister) {
    return <RegisterPanel onDone={(u) => { setUser(u); setShowRegister(false); }} />;
  }

  return (
    <div className="mythos-ink-bg min-h-screen" data-testid="bharat-quest-page">
      {/* Hero strip with user stats */}
      <StatHero user={user} onReload={reload} onLogout={() => { clearUsername(); setUser(null); }} />

      {/* Tabs */}
      <div className="sticky top-[68px] z-20 mythos-ink-bg border-b mythos-rule" data-testid="bq-tabs">
        <div className="max-w-7xl mx-auto px-5 md:px-10 flex overflow-x-auto thin-scroll gap-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`bq-tab-${t.id}`}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold whitespace-nowrap transition relative ${
                  active ? "text-[#C9A66B]" : "text-[#F4EFE6]/60 hover:text-[#F4EFE6]"
                }`}
              >
                <Icon size={16} />
                {t.label}
                {active && (
                  <motion.div
                    layoutId="bq-tab-indicator"
                    className="absolute bottom-0 left-0 right-0 h-[2px] mythos-gold-bg"
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab content */}
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
          >
            {tab === "quest" && <DailyQuestPanel user={user} onReload={reload} />}
            {tab === "map" && <RegionMap user={user} />}
            {tab === "personalities" && <PersonalityChat user={user} onXpEarned={reload} />}
            {tab === "leaderboard" && <Leaderboard meUsername={user.username} />}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

// ============= REGISTER PANEL =============
function RegisterPanel({ onDone }) {
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (username.length < 3) { setErr("Username must be at least 3 characters."); return; }
    setLoading(true); setErr("");
    try {
      const u = await BQ.register(username.trim().toLowerCase(), displayName.trim() || username);
      storeUsername(u.username);
      onDone(u);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Could not register. Try a different name.");
    } finally { setLoading(false); }
  };

  return (
    <div className="mythos-ink-bg min-h-screen" data-testid="bq-register">
      <div className="max-w-3xl mx-auto px-5 md:px-10 py-16 md:py-24">
        <div className="text-[11px] tracking-[0.24em] uppercase mythos-gold font-bold mb-4">Bharat Quest</div>
        <h1 className="font-mythos text-4xl md:text-6xl font-extrabold tracking-tight leading-[1.04] text-[#F4EFE6] max-w-2xl">
          Play through a <span className="mythos-gold">civilization.</span>
        </h1>
        <p className="font-mythos text-lg text-[#E8EBF0]/75 mt-6 max-w-2xl leading-relaxed">
          8 civilizational regions to unlock. 6 legends to converse with. One daily mystery you cannot let your streak miss.
          Pick a handle, climb the global leaderboard, share your Cultural Intelligence Score.
        </p>

        <form onSubmit={submit} className="mt-10 vault-card rounded-2xl p-7 md:p-9 max-w-xl" data-testid="bq-register-form">
          <label className="block">
            <div className="text-[11px] tracking-[0.22em] uppercase mythos-gold font-bold mb-2">Your Player Handle</div>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="e.g. nalanda_scholar"
              maxLength={24}
              minLength={3}
              pattern="[a-zA-Z0-9_\-]{3,24}"
              required
              className="w-full bg-[#0F1623] border border-[#2A3349] focus:border-[#C9A66B] outline-none rounded-full px-5 py-3 text-[#F4EFE6] placeholder-[#7d8294] font-mono"
              data-testid="bq-username-input"
            />
            <div className="text-xs text-[#7d8294] mt-2">3-24 chars. Letters, numbers, _ and - only. This is your public identity on the leaderboard.</div>
          </label>
          <label className="block mt-5">
            <div className="text-[11px] tracking-[0.22em] uppercase mythos-gold font-bold mb-2">Display Name <span className="text-[#7d8294] normal-case tracking-normal">(optional)</span></div>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="What should we call you?"
              maxLength={40}
              className="w-full bg-[#0F1623] border border-[#2A3349] focus:border-[#C9A66B] outline-none rounded-full px-5 py-3 text-[#F4EFE6] placeholder-[#7d8294]"
              data-testid="bq-displayname-input"
            />
          </label>
          {err && <div className="text-[#ff9b9b] text-sm mt-4" data-testid="bq-register-error">{err}</div>}
          <button
            type="submit"
            disabled={loading}
            className="mt-6 mythos-gold-bg text-[#0E0F12] rounded-full px-8 py-3 font-semibold inline-flex items-center gap-2 hover:opacity-90 transition disabled:opacity-60"
            data-testid="bq-register-submit"
          >
            {loading ? <Loader2 className="animate-spin" size={16} /> : <Sparkles size={16} />}
            Begin the Quest
          </button>
        </form>

        <div className="grid sm:grid-cols-3 gap-4 mt-12 max-w-3xl" data-testid="bq-register-pillars">
          {[
            { icon: Compass, t: "8 Civilizations", d: "Indus Valley to Ahom. Unlock as you earn XP." },
            { icon: Users, t: "6 Legends", d: "Chat with Chanakya, Aryabhata, Kalam & more." },
            { icon: Trophy, t: "Global Leaderboard", d: "Climb the world's CIQ rankings. Share your card." },
          ].map((p, i) => (
            <div key={i} className="border border-[#2A3349] rounded-xl p-5">
              <p.icon className="mythos-gold mb-3" size={22} />
              <div className="font-mythos font-bold text-[#F4EFE6]">{p.t}</div>
              <div className="text-sm text-[#E8EBF0]/65 mt-1">{p.d}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============= STAT HERO =============
function StatHero({ user, onReload, onLogout }) {
  const level = user.level;
  const pctToNext = level.next_threshold
    ? Math.min(100, Math.round(((user.ciq - level.band_min) / (level.next_threshold - level.band_min)) * 100))
    : 100;

  return (
    <div className="border-b mythos-rule" data-testid="bq-stat-hero">
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-8">
        <div className="flex flex-wrap items-end justify-between gap-5">
          <div>
            <div className="text-[11px] tracking-[0.24em] uppercase mythos-gold font-bold mb-2">{level.title}</div>
            <h1 className="font-mythos text-3xl md:text-5xl font-extrabold tracking-tight text-[#F4EFE6]">
              @{user.display_name || user.username}
            </h1>
            <div className="text-sm text-[#E8EBF0]/55 mt-1 font-mono">CIQ {user.ciq} · {user.xp} XP · {user.regions_unlocked.length}/8 regions</div>
          </div>
          <div className="flex items-center gap-2">
            <ShareCardButton username={user.username} />
            <button onClick={onLogout} className="text-xs text-[#E8EBF0]/45 hover:text-[#F4EFE6] px-3 py-2" data-testid="bq-logout">Switch player</button>
          </div>
        </div>

        {/* Progress bar to next level */}
        {level.next_title && (
          <div className="mt-6 max-w-2xl" data-testid="bq-progress">
            <div className="flex items-center justify-between text-xs text-[#E8EBF0]/60 mb-2">
              <span>{level.title}</span>
              <span>{level.next_title} · {level.next_threshold} CIQ</span>
            </div>
            <div className="h-2 bg-[#1A2436] rounded-full overflow-hidden">
              <motion.div
                className="h-full mythos-gold-bg"
                initial={{ width: 0 }}
                animate={{ width: `${pctToNext}%` }}
                transition={{ duration: 0.6, ease: "easeOut" }}
              />
            </div>
          </div>
        )}

        {/* Stat chips */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6 max-w-3xl">
          <StatChip label="STREAK" value={`${user.streak}🔥`} sub={user.streak > 0 ? `Best ${user.best_streak}` : "Start today"} />
          <StatChip label="XP" value={user.xp} sub={`+${50 - (user.xp % 50)} to next mile`} />
          <StatChip label="CIQ" value={user.ciq} sub={level.title} />
          <StatChip label="BADGES" value={(user.badges || []).length} sub="Unlock more" />
        </div>
      </div>
    </div>
  );
}

function StatChip({ label, value, sub }) {
  return (
    <div className="vault-card rounded-xl p-4" data-testid={`bq-stat-${label.toLowerCase()}`}>
      <div className="text-[10px] tracking-[0.18em] uppercase mythos-gold font-bold">{label}</div>
      <div className="font-mythos text-2xl md:text-3xl font-extrabold text-[#F4EFE6] mt-1">{value}</div>
      <div className="text-[11px] text-[#E8EBF0]/55 mt-0.5">{sub}</div>
    </div>
  );
}

// ============= DAILY QUEST PANEL =============
function DailyQuestPanel({ user, onReload }) {
  const [quest, setQuest] = useState(null);
  const [answer, setAnswer] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    BQ.daily(user.username).then((d) => {
      setQuest(d.quest);
      if (d.already_completed) {
        setResult({ already_completed: true });
      }
      setLoading(false);
    });
  }, [user.username]);

  const submit = async () => {
    if (!answer.trim()) return;
    setSubmitting(true);
    try {
      const r = await BQ.completeQuest(user.username, quest.id, answer);
      setResult(r);
      onReload();
    } catch (e) {
      setResult({ error: e?.response?.data?.detail || "Submission failed" });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="text-[#E8EBF0]/55 py-12 text-center"><Loader2 className="inline animate-spin" /></div>;

  return (
    <div data-testid="bq-daily-panel">
      <div className="text-[11px] tracking-[0.24em] uppercase mythos-gold font-bold mb-3">Today's Mystery</div>
      <h2 className="font-mythos text-3xl md:text-4xl font-extrabold tracking-tight text-[#F4EFE6] max-w-3xl">
        {quest.prompt}
      </h2>
      <div className="mt-3 flex items-center gap-2 text-xs text-[#E8EBF0]/55">
        <Sparkles size={12} className="mythos-gold" />
        <span>+{quest.xp} XP · resets at 00:00 UTC · keep your 🔥 streak alive</span>
      </div>

      {result?.already_completed ? (
        <div className="vault-card rounded-xl p-6 mt-8 max-w-2xl" data-testid="bq-already-completed">
          <Check className="mythos-gold mb-3" size={28} />
          <div className="font-mythos text-xl font-bold text-[#F4EFE6]">You've already faced today's mystery.</div>
          <div className="text-sm text-[#E8EBF0]/65 mt-2">Come back tomorrow to keep your streak. While you wait, talk to a legend or unlock a region.</div>
        </div>
      ) : result?.correct !== undefined ? (
        <ResultCard result={result} questAnswer={result.correct_answer} hint={quest.hint} />
      ) : (
        <div className="mt-8 max-w-2xl">
          {quest.hint && (
            <div className="text-sm text-[#C9A66B]/70 italic mb-3" data-testid="bq-hint">Hint: {quest.hint}</div>
          )}
          <div className="flex gap-2">
            <input
              type="text"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              placeholder="Type your answer…"
              className="flex-1 bg-[#0F1623] border border-[#2A3349] focus:border-[#C9A66B] outline-none rounded-full px-5 py-3 text-[#F4EFE6] placeholder-[#7d8294] text-lg"
              data-testid="bq-quest-input"
              disabled={submitting}
            />
            <button
              onClick={submit}
              disabled={!answer.trim() || submitting}
              className="mythos-gold-bg text-[#0E0F12] rounded-full px-6 py-3 font-semibold disabled:opacity-50 transition"
              data-testid="bq-quest-submit"
            >
              {submitting ? <Loader2 className="animate-spin" size={16} /> : "Submit"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ResultCard({ result, questAnswer, hint }) {
  const correct = result.correct;
  return (
    <motion.div
      initial={{ scale: 0.95, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className={`vault-card rounded-2xl p-7 mt-8 max-w-2xl border-2 ${correct ? "border-[#C9A66B]" : "border-[#3A3D44]"}`}
      data-testid="bq-result"
    >
      <div className={`w-14 h-14 rounded-full grid place-items-center mb-4 ${correct ? "mythos-gold-bg" : "bg-[#3A3D44]"}`}>
        {correct ? <Check className="text-[#0E0F12]" size={28} /> : <X className="text-[#F4EFE6]" size={28} />}
      </div>
      <h3 className="font-mythos text-2xl font-extrabold text-[#F4EFE6]">
        {correct ? "Correct!" : "Not quite."}
      </h3>
      <div className="text-sm text-[#E8EBF0]/70 mt-2">
        {correct
          ? <>You earned <span className="mythos-gold font-bold">+{result.xp_earned} XP</span> and <span className="mythos-gold font-bold">+{result.ciq_delta} CIQ</span>.</>
          : <>The answer was <span className="mythos-gold font-bold">{questAnswer}</span>. Consolation +10 XP.</>}
      </div>
      {result.bonus_xp > 0 && (
        <div className="mt-3 inline-flex items-center gap-2 mythos-gold-bg text-[#0E0F12] rounded-full px-3 py-1 text-xs font-bold" data-testid="bq-streak-bonus">
          <Flame size={12} /> Streak milestone! +{result.bonus_xp} bonus XP
        </div>
      )}
      <div className="mt-5 text-sm text-[#E8EBF0]/65" data-testid="bq-result-streak">
        Streak: <span className="text-[#F4EFE6] font-bold">{result.streak}🔥</span> · Total CIQ: <span className="text-[#F4EFE6] font-bold">{result.user.ciq}</span>
      </div>
      <div className="mt-6">
        <ShareCardButton username={result.user.username} variant="prominent" />
      </div>
    </motion.div>
  );
}

// ============= SHARE BUTTON =============
function ShareCardButton({ username, variant = "default" }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = `${window.location.origin}/bharat-quest?ref=${username}`;
  const cardUrl = BQ.cardUrl(username);
  const message = encodeURIComponent(`Check my Cultural Intelligence Score on Bharat Quest 🇮🇳 ${shareUrl}`);

  const copy = async () => {
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const big = variant === "prominent";
  return (
    <div className="inline-flex flex-wrap items-center gap-2" data-testid="bq-share-actions">
      <a
        href={`https://wa.me/?text=${message}`}
        target="_blank"
        rel="noreferrer"
        className={`inline-flex items-center gap-2 mythos-gold-bg text-[#0E0F12] rounded-full font-semibold transition hover:opacity-90 ${big ? "px-5 py-2.5" : "px-4 py-2 text-sm"}`}
        data-testid="bq-share-whatsapp"
      >
        <Share2 size={big ? 16 : 14} /> Share to WhatsApp
      </a>
      <a
        href={`https://twitter.com/intent/tweet?text=${message}`}
        target="_blank"
        rel="noreferrer"
        className={`inline-flex items-center gap-2 border border-[#3A3D44] hover:border-[#C9A66B] text-[#F4EFE6] rounded-full font-semibold transition ${big ? "px-5 py-2.5" : "px-4 py-2 text-sm"}`}
        data-testid="bq-share-twitter"
      >
        Share to X
      </a>
      <button
        onClick={copy}
        className={`inline-flex items-center gap-2 text-[#E8EBF0]/65 hover:text-[#F4EFE6] transition ${big ? "px-4 py-2.5 text-sm" : "px-3 py-2 text-xs"}`}
        data-testid="bq-share-copy"
      >
        {copied ? <Check size={14} className="mythos-gold" /> : <Copy size={14} />}
        {copied ? "Copied" : "Copy link"}
      </button>
      <a
        href={cardUrl}
        target="_blank"
        rel="noreferrer"
        className="text-xs text-[#7d8294] hover:text-[#C9A66B] underline-offset-2 hover:underline"
        data-testid="bq-share-card-png"
      >
        Preview card →
      </a>
    </div>
  );
}
